import lodash from 'lodash'
import {
  GeckoHorizontalWeld,
  GeckoHorizontalWeldJSON,
  GeckoVerticalWeld,
  GeckoVerticalWeldJSON,
} from 'types'
import {
  GeckoComponentModelType,
  GeckoFeatureModelType,
} from 'types/assetModel/assetModelEnums'
import { GeckoEditableComponent } from 'types/assetModel/components/geckoEditableComponent'
import {
  GeckoFixedSensor,
  GeckoFixedSensorJSON,
} from 'types/assetModel/features/geckoFixedSensor'
import {
  GeckoNozzle,
  GeckoNozzleJSON,
} from 'types/assetModel/features/geckoNozzle'
import {
  GeckoPolygonalPlate,
  GeckoPolygonalPlateJSON,
} from 'types/assetModel/features/geckoPolygonalPlate'
import {
  GeckoShipDeckBitt,
  GeckoShipDeckBittJSON,
} from 'types/assetModel/features/shipDeck/geckoShipDeckBitt'
import {
  GeckoShipDeckChock,
  GeckoShipDeckChockJSON,
} from 'types/assetModel/features/shipDeck/geckoShipDeckChock'
import {
  GeckoShipDeckHatch,
  GeckoShipDeckHatchJSON,
} from 'types/assetModel/features/shipDeck/geckoShipDeckHatch'
import {
  ExternalUserFieldAccess,
  FormField,
  getFormFieldByDataKey,
  InputType,
  UnitMagnitude,
} from 'types/geckoFormTypesAndHelpers'
import { GeckoComponentRect } from 'types/geometric/geckoComponentRect'
import { GeckoMeasurement } from 'types/measurement/geckoMeasurement'
import { GeckoMeasurementUnit } from 'types/measurement/geckoMeasurementUnit'
import { assetModelPrecision } from 'utils/constants'
import { requiredNumberSchema, requiredStringSchema } from 'utils/zodHelpers'

type ShipDeckSupportedFeatures =
  | GeckoVerticalWeldJSON
  | GeckoHorizontalWeldJSON
  | GeckoNozzleJSON
  | GeckoFixedSensorJSON
  | GeckoShipDeckBittJSON
  | GeckoShipDeckChockJSON
  | GeckoShipDeckHatchJSON
  | GeckoPolygonalPlateJSON

export type ShipDeckAssetModelJSON = {
  name: string
  type: GeckoComponentModelType.ShipDeck
  length: number
  start_width: number
  end_width: number
  features: Record<string, ShipDeckSupportedFeatures>
  scene_config: Record<string, unknown>
  component_bounds: Record<string, number>
}

export const ShipDeckAvailableFeatures: GeckoFeatureModelType[] = [
  GeckoFeatureModelType.ShipDeckChock,
  GeckoFeatureModelType.ShipDeckBitt,
  GeckoFeatureModelType.ShipDeckHatch,
  GeckoFeatureModelType.PolygonalPlate,
]

export const NewShipDeckAssetModelJSON = {
  name: 'New Ship Deck',
  type: GeckoComponentModelType.ShipDeck,
  length: 0,
  start_width: 0,
  end_width: 0,
  features: {},
  scene_config: {},
  component_bounds: {
    x_start: 0,
    y_start: 0,
    x_end: 0,
    y_end: 0,
  },
} as ShipDeckAssetModelJSON

export class GeckoShipDeck extends GeckoEditableComponent {
  type: GeckoComponentModelType.ShipDeck
  length: GeckoMeasurement
  startWidth: GeckoMeasurement
  endWidth: GeckoMeasurement
  componentBounds: GeckoComponentRect
  sceneConfig = undefined
  polygonalPlates: Map<string, GeckoPolygonalPlate>
  horizontalWelds: Map<string, GeckoHorizontalWeld>
  verticalWelds: Map<string, GeckoVerticalWeld>
  nozzles: Map<string, GeckoNozzle>
  fixedSensors: Map<string, GeckoFixedSensor>
  chocks: Map<string, GeckoShipDeckChock>
  bitts: Map<string, GeckoShipDeckBitt>
  hatches: Map<string, GeckoShipDeckHatch>

  private constructor(
    id: string,
    json: Record<string, unknown>,
    name: string,
    length: GeckoMeasurement,
    startWidth: GeckoMeasurement,
    endWidth: GeckoMeasurement,
    componentBounds: GeckoComponentRect,
    horizontalWelds: Map<string, GeckoHorizontalWeld>,
    verticalWelds: Map<string, GeckoVerticalWeld>,
    nozzles: Map<string, GeckoNozzle>,
    fixedSensors: Map<string, GeckoFixedSensor>,
    chocks: Map<string, GeckoShipDeckChock>,
    bitts: Map<string, GeckoShipDeckBitt>,
    hatches: Map<string, GeckoShipDeckHatch>,
    polygonalPlates: Map<string, GeckoPolygonalPlate>
  ) {
    super(id, name, json)
    this.type = GeckoComponentModelType.ShipDeck
    this.length = length
    this.startWidth = startWidth
    this.endWidth = endWidth
    this.componentBounds = componentBounds
    this.polygonalPlates = polygonalPlates
    this.horizontalWelds = horizontalWelds
    this.verticalWelds = verticalWelds
    this.nozzles = nozzles
    this.fixedSensors = fixedSensors
    this.chocks = chocks
    this.bitts = bitts
    this.hatches = hatches
  }

  public get maxWidth() {
    return this.startWidth.num >= this.endWidth.num
      ? this.startWidth
      : this.endWidth
  }

  public get minWidth() {
    return this.startWidth.num < this.endWidth.num
      ? this.startWidth
      : this.endWidth
  }

  public get polygonalPlatesArray() {
    return Array.from(this.polygonalPlates.values())
  }

  public get verticalWeldsArray() {
    return Array.from(this.verticalWelds.values())
  }

  public get horizontalWeldsArray() {
    return Array.from(this.horizontalWelds.values())
  }

  public get nozzlesArray() {
    return Array.from(this.nozzles.values())
  }

  public get fixedSensorsArray() {
    return Array.from(this.fixedSensors.values())
  }

  public get chocksArray() {
    return Array.from(this.chocks.values())
  }

  public get bittsArray() {
    return Array.from(this.bitts.values())
  }

  public get hatchesArray() {
    return Array.from(this.hatches.values())
  }

  public static CreateFromJSON(id: string, json: ShipDeckAssetModelJSON) {
    const name = json.name as string

    const length = GeckoMeasurement.CreateFromValue(
      json.length as number,
      GeckoMeasurementUnit.Meters
    )
    const startWidth = GeckoMeasurement.CreateFromValue(
      json.start_width as number,
      GeckoMeasurementUnit.Meters
    )
    const endWidth = GeckoMeasurement.CreateFromValue(
      json.end_width as number,
      GeckoMeasurementUnit.Meters
    )

    const componentBoundsData = json.component_bounds as Record<string, number>
    const componentBounds = GeckoComponentRect.CreateFromXYs(
      id,
      componentBoundsData.x_start,
      componentBoundsData.y_start,
      componentBoundsData.x_end,
      componentBoundsData.y_end,
      GeckoMeasurementUnit.Meters,
      GeckoMeasurementUnit.Meters
    )

    const featureData = json.features as Record<
      string,
      ShipDeckSupportedFeatures
    >

    const childHorizontalWeldJSON = lodash.pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.HorizontalWeld
    })
    const horizontalWelds = GeckoHorizontalWeld.CreateMapFromJSON(
      id,
      childHorizontalWeldJSON as Record<string, GeckoHorizontalWeldJSON>
    )

    const childVerticalWeldJSON = lodash.pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.VerticalWeld
    })
    const verticalWelds = GeckoVerticalWeld.CreateMapFromJSON(
      id,
      childVerticalWeldJSON as Record<string, GeckoVerticalWeldJSON>
    )

    const childNozzleJSON = lodash.pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.Nozzle
    })
    const nozzles = GeckoNozzle.CreateMapFromJSON(
      id,
      childNozzleJSON as Record<string, GeckoNozzleJSON>
    )

    const childFixedSensorJSON = lodash.pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.FixedSensor
    })
    const fixedSensors = GeckoFixedSensor.CreateMapFromJSON(
      id,
      childFixedSensorJSON as Record<string, GeckoFixedSensorJSON>
    )

    const childChockJSON = lodash.pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.ShipDeckChock
    })

    const chocks = GeckoShipDeckChock.CreateMapFromJSON(
      id,
      childChockJSON as Record<string, GeckoShipDeckChockJSON>
    )

    const childBittJSON = lodash.pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.ShipDeckBitt
    })

    const bitts = GeckoShipDeckBitt.CreateMapFromJSON(
      id,
      childBittJSON as Record<string, GeckoShipDeckBittJSON>
    )

    const childHatchJSON = lodash.pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.ShipDeckHatch
    })

    const hatches = GeckoShipDeckHatch.CreateMapFromJSON(
      id,
      childHatchJSON as Record<string, GeckoShipDeckHatchJSON>
    )

    const childPolygonalPlateJSON = lodash.pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.PolygonalPlate
    })
    const polygonalPlates = GeckoPolygonalPlate.CreateMapFromJSON(
      id,
      childPolygonalPlateJSON as Record<string, GeckoPolygonalPlateJSON>
    )

    return new GeckoShipDeck(
      id,
      json,
      name,
      length,
      startWidth,
      endWidth,
      componentBounds,
      horizontalWelds,
      verticalWelds,
      nozzles,
      fixedSensors,
      chocks,
      bitts,
      hatches,
      polygonalPlates
    )
  }

  public asJSON(): ShipDeckAssetModelJSON {
    return generateShipDeck(
      this.name,
      this.length,
      this.componentBounds.y.start,
      this.startWidth,
      this.endWidth,
      this.json as ShipDeckAssetModelJSON
    )
  }
}

const generateShipDeck = (
  name: string,
  length: GeckoMeasurement,
  yStart: GeckoMeasurement,
  startWidth: GeckoMeasurement,
  endWidth: GeckoMeasurement,
  otherShipDeckData: ShipDeckAssetModelJSON = NewShipDeckAssetModelJSON
): ShipDeckAssetModelJSON => {
  const deckYStart = yStart
    .convertTo(GeckoMeasurementUnit.Meters)
    .toPrecision(assetModelPrecision).num
  const deckLength = length
    .convertTo(GeckoMeasurementUnit.Meters)
    .toPrecision(assetModelPrecision).num

  const deckStartWidth = startWidth
    .convertTo(GeckoMeasurementUnit.Meters)
    .toPrecision(assetModelPrecision).num
  const deckEndWidth = endWidth
    .convertTo(GeckoMeasurementUnit.Meters)
    .toPrecision(assetModelPrecision).num

  const maxWidth = Math.max(deckStartWidth, deckEndWidth)
  const halfMaxWidth = Number((maxWidth / 2).toPrecision(assetModelPrecision))

  return {
    name: name,
    type: GeckoComponentModelType.ShipDeck,
    length: deckLength,
    start_width: deckStartWidth,
    end_width: deckEndWidth,
    features: otherShipDeckData.features,
    scene_config: otherShipDeckData.scene_config,
    component_bounds: {
      x_start: -halfMaxWidth,
      y_start: deckYStart,
      x_end: halfMaxWidth,
      y_end: deckYStart + deckLength,
    },
  }
}

export const ShipDeckAssetModelFormFields = (
  shipDeckJSON: ShipDeckAssetModelJSON
) => {
  return [
    {
      dataKey: 'name',
      label: 'Ship Deck Name',
      value: shipDeckJSON.name,
      unit_magnitude: null,
      validationRule: requiredStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    {
      dataKey: 'y_start',
      label: 'Y Start',
      value: shipDeckJSON.component_bounds.y_start,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      initialValue: 0,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'length',
      label: 'Length',
      value: shipDeckJSON.length,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'start_width',
      label: 'Start Width',
      value: shipDeckJSON.start_width,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'end_width',
      label: 'End Width',
      value: shipDeckJSON.end_width,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
  ]
}

export const ShipDeckJSONFromFormFields = (
  component: ShipDeckAssetModelJSON,
  formFields: FormField[]
) => {
  return generateShipDeck(
    getFormFieldByDataKey(formFields, 'name')?.value as string,
    GeckoMeasurement.CreateFromValue(
      getFormFieldByDataKey(formFields, 'length')?.value as number,
      GeckoMeasurementUnit.Meters
    ),
    GeckoMeasurement.CreateFromValue(
      getFormFieldByDataKey(formFields, 'y_start')?.value as number,
      GeckoMeasurementUnit.Meters
    ),
    GeckoMeasurement.CreateFromValue(
      getFormFieldByDataKey(formFields, 'start_width')?.value as number,
      GeckoMeasurementUnit.Meters
    ),
    GeckoMeasurement.CreateFromValue(
      getFormFieldByDataKey(formFields, 'end_width')?.value as number,
      GeckoMeasurementUnit.Meters
    ),
    component
  )
}
